/**
 * The organization settings page kit: one page header (title, a line of
 * description, actions on the right), grouped cards of rows with the label
 * and description on the left and the control on the right, and the usage
 * meter the allowance figures draw with. Every organization page builds from
 * these, so they share spacing and type rather than each inventing its own.
 *
 * @module components/settings/settingsCard
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { cn } from "../../lib/utils";
import { formatGbp } from "../../usage/usageFormat";
import { SettingsRow, useSettingsSearchTarget } from "./settingsLayout";
import { usageMeterGeometry, type UsageMeterTone } from "./orgMembers.logic";

export function SettingsPageHeader({
  title,
  description,
  actions,
}: {
  readonly title: string;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 px-1 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 space-y-1.5">
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="max-w-2xl text-[13px] leading-[1.5] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/**
 * A titled group of rows on one surface. Rows are divided by hairlines; the
 * title and description sit above the card, outside it, like Linear's and
 * Vercel's settings.
 */
export function SettingsCard({
  title,
  description,
  action,
  footer,
  children,
  className,
  bodyClassName,
  ...sectionProps
}: Omit<ComponentPropsWithoutRef<"section">, "title"> & {
  readonly title?: string;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly footer?: ReactNode;
  readonly bodyClassName?: string;
}) {
  const targetRef = useSettingsSearchTarget<HTMLElement>(sectionProps.id);
  return (
    <section
      {...sectionProps}
      ref={targetRef}
      tabIndex={sectionProps.id ? -1 : sectionProps.tabIndex}
      className={cn("space-y-2.5 outline-none", className)}
    >
      {title || action ? (
        <div className="flex items-end justify-between gap-4 px-1">
          <div className="min-w-0 space-y-0.5">
            {title ? (
              <h2 className="text-sm font-medium tracking-[-0.005em] text-foreground">{title}</h2>
            ) : null}
            {description ? (
              <p className="text-[13px] leading-[1.45] text-muted-foreground/90">{description}</p>
            ) : null}
          </div>
          {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
      ) : null}
      <div
        className={cn(
          "overflow-hidden rounded-xl border border-border/70 bg-card/50 shadow-xs/5 dark:bg-card/30",
          "divide-y divide-border/60",
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer ? <div className="px-1">{footer}</div> : null}
    </section>
  );
}

/** A settings row sized for a card: square corners, roomier insets. */
export function SettingsCardRow({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SettingsRow>) {
  return (
    <SettingsRow
      {...props}
      className={cn("rounded-none px-4 sm:px-5", props.children ? "pt-4 pb-2" : "py-4", className)}
    />
  );
}

/** The included fill is the workspace's Flash blue, as on the usage page. */
const INCLUDED_FILL = "#6aa1d8";

const TONE_FILL: Readonly<Record<UsageMeterTone, string | null>> = {
  normal: null,
  near: "bg-warning",
  over: "bg-warning",
  blocked: "bg-error",
};

/**
 * Spend against an allowance. Within it: one track, one fill (amber from 90%).
 * Past it: the allowance track, full, and the excess as a second segment on
 * the same scale — amber while billed on demand, red when usage is paused.
 */
export function UsageMeter({
  spentGbp,
  includedGbp,
  blocked = false,
  label,
  size = "default",
  className,
}: {
  readonly spentGbp: number;
  readonly includedGbp: number;
  readonly blocked?: boolean;
  /** Accessible name, e.g. "Organization usage this month". */
  readonly label: string;
  readonly size?: "default" | "sm";
  readonly className?: string;
}) {
  const meter = usageMeterGeometry(spentGbp, includedGbp, { blocked });
  const fill = TONE_FILL[meter.tone];
  const past = meter.overShare > 0;
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={includedGbp}
      aria-valuenow={Math.min(spentGbp, includedGbp)}
      aria-valuetext={`${formatGbp(spentGbp)} of ${formatGbp(includedGbp)}${
        past ? `, ${formatGbp(spentGbp - includedGbp)} over` : ""
      }`}
      className={cn("flex w-full items-stretch", size === "sm" ? "h-1" : "h-1.5", className)}
    >
      <div
        className="h-full overflow-hidden rounded-full bg-foreground/[0.08]"
        style={{ width: `${(meter.includedShare * 100).toFixed(2)}%` }}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-500",
            past ? null : fill,
          )}
          style={{
            width: `${(meter.usedShare * 100).toFixed(2)}%`,
            ...(past || fill === null ? { backgroundColor: INCLUDED_FILL } : {}),
          }}
        />
      </div>
      {past ? (
        <div
          className={cn("h-full rounded-full border-l-2 border-background", fill)}
          style={{ width: `${(meter.overShare * 100).toFixed(2)}%` }}
        />
      ) : null}
    </div>
  );
}

/** A figure with a small label above it, for the summary strip of a card. */
export function SettingsStat({
  label,
  value,
  hint,
  className,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  readonly className?: string;
}) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-lg font-semibold tracking-[-0.01em] text-foreground tabular-nums">
        {value}
      </p>
      {hint ? <p className="truncate text-xs text-muted-foreground/80">{hint}</p> : null}
    </div>
  );
}
